import { useLogin } from '@refinedev/core'
import { Button, Form, Input, Typography } from 'antd'
import { CloseOutlined, ExclamationCircleFilled, LockOutlined, UserOutlined } from '@ant-design/icons'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { Footer } from '@/components/footer'
import { ThemeToggle } from '@/components/theme-toggle'
import { useThemeMode } from '@/theme/theme-provider'

const { Title, Text } = Typography

/**
 * 登录页（F1）
 *
 * 全屏绿色渐变背景 + 右侧悬浮表单卡片设计
 * - 品牌区背景铺满整个视口（绿色渐变 + 装饰图形）
 * - 表单卡片 85% 不透明度 + 双层阴影悬浮效果，浮于背景之上
 * - 暗色模式渐变更深沉（深绿色调），保持设计一致性
 */

/** 亮色模式绿色渐变（鲜明活泼） */
const LIGHT_GRADIENT = 'linear-gradient(135deg, #059669 0%, #10B981 50%, #34D399 100%)'
/** 暗色模式绿色渐变（深沉内敛） */
const DARK_GRADIENT = 'linear-gradient(135deg, #022C22 0%, #064E3B 50%, #065F46 100%)'

/** 品牌区装饰图形（铺满整个视口的背景装饰） */
function BrandDecoration() {
  return (
    <svg
      width="100%"
      height="100%"
      style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.15,
        pointerEvents: 'none'
      }}
      viewBox="0 0 800 600"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
      <circle cx="600" cy="120" r="180" fill="white" opacity="0.08" />
      <circle cx="120" cy="480" r="120" fill="white" opacity="0.06" />
    </svg>
  )
}

/** 品牌区特性列表 */
function FeatureList() {
  const features: Array<{ title: string; desc: string }> = [
    {
      title: 'Ed25519 签名验证',
      desc: '客户端零信任校验，私钥永不离开服务器'
    },
    {
      title: '订单号发卡',
      desc: '一单一码，激活时自动绑定设备，杜绝转售'
    },
    {
      title: '换机重绑 + 退款吊销',
      desc: '年度解绑上限，按订单号追溯'
    }
  ]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 480 }}>
      {features.map((f) => (
        <div key={f.title} style={{ display: 'flex', gap: 12 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: 16
            }}
          >
            ✓
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>{f.title}</div>
            <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>{f.desc}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

interface LoginFormValues {
  username: string
  password: string
}

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <label
      style={{
        display: 'block',
        fontSize: 13,
        fontWeight: 500,
        color: 'var(--ant-color-text-secondary, #475569)',
        marginBottom: 6
      }}
    >
      {children}
    </label>
  )
}

export default function LoginPage() {
  const [form] = Form.useForm<LoginFormValues>()
  const { mutate: login, isLoading } = useLogin<LoginFormValues>()
  const navigate = useNavigate()
  const { mode } = useThemeMode()
  const isDark = mode === 'dark'
  const [loginError, setLoginError] = useState<string | null>(null)

  // 登录失败时：在用户名和密码字段上同时标记错误（避免泄露具体哪个错误）
  const applyFieldErrors = (msg: string) => {
    form.setFields([
      { name: 'username', errors: [msg] },
      { name: 'password', errors: [msg] }
    ])
  }

  // 用户输入时：清除对应字段的错误状态与登录错误提示
  const handleFieldChange = () => {
    if (loginError) setLoginError(null)
    const usernameErrors = form.getFieldError('username')
    const passwordErrors = form.getFieldError('password')
    if (usernameErrors.length || passwordErrors.length) {
      form.setFields([
        { name: 'username', errors: [] },
        { name: 'password', errors: [] }
      ])
    }
  }

  // 动态样式：跟随主题切换渐变背景 + 表单卡片悬浮效果
  const styles = useMemo(() => {
    // 页面背景：绿色渐变铺满全界面
    const pageBg = isDark ? DARK_GRADIENT : LIGHT_GRADIENT

    // 表单卡片：绿色调背景 + 绿色边框（框），调低透明度让底层绿色渐变微微透出
    // 亮色：Emerald-50（极浅绿）85% 不透明
    // 暗色：Emerald-900（深绿）88% 不透明，确保暗色模式下背景为深色而非白色
    const cardBg = isDark ? 'rgba(6, 78, 59, 0.88)' : 'rgba(236, 253, 245, 0.85)'

    // 表单卡片边框：明显的绿色框，作为"框"增强层次感
    const cardBorder = isDark
      ? '2px solid rgba(52, 211, 153, 0.45)'
      : '2px solid rgba(16, 185, 129, 0.45)'

    // 表单卡片悬浮效果：双层阴影
    // 第一层：远距离大模糊半径阴影（投影深远，营造悬浮感）
    // 第二层：近距离小模糊半径阴影（边缘清晰，增强立体感）
    const cardShadow = isDark
      ? '0 24px 60px -12px rgba(0, 0, 0, 0.7), 0 8px 24px -4px rgba(0, 0, 0, 0.5)'
      : '0 24px 60px -12px rgba(15, 23, 42, 0.25), 0 8px 24px -4px rgba(15, 23, 42, 0.15)'

    // 主题切换按钮容器：与表单卡片同色调，浮于表单卡片右上角
    const toggleWrapperBg = isDark ? 'rgba(6, 78, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)'
    const toggleWrapperBorder = isDark
      ? '1px solid rgba(52, 211, 153, 0.4)'
      : '1px solid rgba(16, 185, 129, 0.3)'
    const toggleWrapperShadow = isDark
      ? '0 2px 8px -2px rgba(0, 0, 0, 0.4)'
      : '0 2px 8px -2px rgba(15, 23, 42, 0.08)'

    // 错误弹窗：表单卡片下方悬浮小弹窗，与表单卡片同等的悬浮质感
    // 红色调配色 + 双层阴影 + 毛玻璃模糊，避免突兀改变原表单卡片布局
    const errorPopupBg = isDark ? 'rgba(127, 29, 29, 0.92)' : 'rgba(254, 226, 226, 0.95)'
    const errorPopupBorder = isDark
      ? '1px solid rgba(248, 113, 113, 0.5)'
      : '1px solid rgba(239, 68, 68, 0.4)'
    const errorPopupShadow = isDark
      ? '0 16px 40px -8px rgba(0, 0, 0, 0.6), 0 4px 12px -2px rgba(0, 0, 0, 0.4)'
      : '0 16px 40px -8px rgba(15, 23, 42, 0.18), 0 4px 12px -2px rgba(15, 23, 42, 0.1)'

    return {
      page: {
        display: 'flex',
        minHeight: '100vh',
        background: pageBg,
        position: 'relative' as const,
        overflow: 'hidden' as const
      } as const,
      container: {
        display: 'flex',
        width: '100%',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '64px 56px',
        position: 'relative' as const,
        zIndex: 1,
        gap: 48
      } as const,
      brandSection: {
        flex: '1 1 55%',
        color: 'white',
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        gap: 56,
        height: 'calc(100vh - 128px)'
      } as const,
      formSection: {
        flex: '0 0 420px',
        maxWidth: 420,
        position: 'relative' as const
      } as const,
      formCard: {
        width: '100%',
        padding: 40,
        borderRadius: 16,
        background: cardBg,
        border: cardBorder,
        boxShadow: cardShadow,
        backdropFilter: 'blur(16px)'
      } as const,
      toggleWrapper: {
        position: 'absolute' as const,
        top: 16,
        right: 16,
        zIndex: 2,
        padding: 4,
        borderRadius: 8,
        background: toggleWrapperBg,
        border: toggleWrapperBorder,
        backdropFilter: 'blur(8px)',
        boxShadow: toggleWrapperShadow
      } as const,
      errorPopup: {
        position: 'absolute' as const,
        top: 'calc(100% + 16px)',
        left: 0,
        right: 0,
        padding: '12px 16px',
        borderRadius: 12,
        background: errorPopupBg,
        border: errorPopupBorder,
        boxShadow: errorPopupShadow,
        backdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        zIndex: 10
      } as const
    }
  }, [isDark])

  // 已登录且 token 未过期则跳转首页
  // 注意：只做本地 JWT 过期校验，不发起 API 请求
  //   - 避免 token 已失效时跳转到 / 触发 401 循环（check() 会再调 /me 验证）
  //   - 过期/格式异常时清除本地存储，留在登录页
  useEffect(() => {
    const token = localStorage.getItem('license-admin-token')
    if (!token) return

    try {
      const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number }
      if (payload.exp && payload.exp * 1000 > Date.now()) {
        navigate('/', { replace: true })
      } else {
        // token 已过期，清除本地凭证
        localStorage.removeItem('license-admin-token')
        localStorage.removeItem('license-admin-user')
      }
    } catch {
      // token 格式无效，清除本地凭证
      localStorage.removeItem('license-admin-token')
      localStorage.removeItem('license-admin-user')
    }
  }, [navigate])

  const handleSubmit = (values: LoginFormValues) => {
    setLoginError(null)
    login(values, {
      onSuccess: (data) => {
        // Refine 的 login 不抛错，失败时返回 { success: false, error }
        if (data && (data as { success?: boolean }).success === false) {
          const err = (data as { error?: Error }).error
          const msg = err?.message ?? '用户名或密码错误'
          setLoginError(msg)
          applyFieldErrors(msg)
        }
      }
    })
  }

  // 错误弹窗 5s 后自动消失：同时清除字段错误状态，避免弹窗消失后字段仍标红
  useEffect(() => {
    if (!loginError) return
    const timer = setTimeout(() => {
      setLoginError(null)
      form.setFields([
        { name: 'username', errors: [] },
        { name: 'password', errors: [] }
      ])
    }, 5000)
    return () => clearTimeout(timer)
  }, [loginError, form])

  // 手动关闭错误弹窗
  const handleCloseError = () => {
    setLoginError(null)
    form.setFields([
      { name: 'username', errors: [] },
      { name: 'password', errors: [] }
    ])
  }

  return (
    <div style={styles.page}>
      {/* 装饰图形铺满整个视口 */}
      <BrandDecoration />

      <div style={styles.container}>
        {/* 左侧品牌内容区 */}
        <div style={styles.brandSection}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'rgba(255, 255, 255, 0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 700,
                backdropFilter: 'blur(10px)'
              }}
            >
              L
            </div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>License Platform</div>
          </div>

          {/* 中间标题 + 特性列表 */}
          <div>
            <Title
              level={2}
              style={{
                color: 'white',
                fontSize: 36,
                fontWeight: 700,
                lineHeight: 1.2,
                marginBottom: 16,
                maxWidth: 480
              }}
            >
              通用 License 平台
              <br />
              多项目序列号管理
            </Title>
            <Text
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: 16,
                display: 'block',
                marginBottom: 48,
                maxWidth: 480,
                lineHeight: 1.6
              }}
            >
              一个后端支撑多个付费软件项目。Ed25519 签名、订单号直接发卡、激活时绑定设备、换机重绑、退款吊销 — 全流程可视化管理。
            </Text>

            <FeatureList />
          </div>
        </div>

        {/* 右侧表单卡片（悬浮于绿色渐变背景之上） */}
        <div style={styles.formSection}>
          {/* 主题切换按钮（半透明卡片容器，浮于表单卡片右上角） */}
          <div style={styles.toggleWrapper}>
            <ThemeToggle />
          </div>
          {/* 表单卡片：85% 不透明度 + 双层阴影悬浮效果 */}
          <div style={styles.formCard}>
            <Title level={3} style={{ marginBottom: 8, fontWeight: 600 }}>
              登录管理后台
            </Title>
            <Text
              style={{
                display: 'block',
                marginBottom: 32,
                color: 'var(--ant-color-text-secondary, #475569)',
                fontSize: 14
              }}
            >
              使用管理员账号登录以管理项目和序列号
            </Text>

            <Form<LoginFormValues>
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              onValuesChange={handleFieldChange}
              requiredMark={false}
            >
              <Form.Item
                name="username"
                label={<FieldLabel>用户名</FieldLabel>}
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: 'var(--ant-color-text-quaternary, #94A3B8)' }} />}
                  placeholder="admin"
                  size="large"
                  autoComplete="username"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={<FieldLabel>密码</FieldLabel>}
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: 'var(--ant-color-text-quaternary, #94A3B8)' }} />}
                  placeholder="••••••••"
                  size="large"
                  autoComplete="current-password"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={isLoading}
                  style={{ height: 44, fontWeight: 500 }}
                >
                  登录
                </Button>
              </Form.Item>
            </Form>
          </div>

          {/* 错误弹窗：表单卡片下方悬浮小弹窗，5s 后自动消失 */}
          {loginError && (
            <div style={styles.errorPopup} role="alert">
              <ExclamationCircleFilled
                style={{
                  color: 'var(--ant-color-error, #ef4444)',
                  fontSize: 16,
                  marginTop: 2,
                  flexShrink: 0
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--ant-color-text)' }}>
                  {loginError}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--ant-color-text-secondary, #64748b)',
                    marginTop: 2
                  }}
                >
                  请检查输入的用户名和密码是否正确
                </div>
              </div>
              <CloseOutlined
                style={{
                  color: 'var(--ant-color-text-tertiary, #94a3b8)',
                  fontSize: 12,
                  cursor: 'pointer',
                  flexShrink: 0,
                  marginTop: 3
                }}
                onClick={handleCloseError}
              />
            </div>
          )}
        </div>
      </div>

      {/* 登录页底部 ICP 备案信息 */}
      <Footer variant="light" />
    </div>
  )
}
